import type { PanderrTemplateDefinition } from "../types";

export const CONTRACTOR_MODERN_TEMPLATE: PanderrTemplateDefinition = {
  id: "contractor-modern",
  name: "Contractor Modern",
  description:
    "A bold, project-driven website for contractors, builders, remodelers, and construction companies.",
  category: "contractor",

  sections: [
    {
      id: "hero",
      type: "hero",
      eyebrow: "Built right. Built to last.",
      heading: "Quality construction without the guesswork.",
      body: "Professional workmanship, straightforward communication, and dependable project management from estimate to final walkthrough.",
      images: [
        {
          src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
          alt: "Construction professional working on a residential project",
          role: "hero",
        },
      ],
      buttons: [
        {
          label: "Request an Estimate",
          href: "/contact/",
          variant: "primary",
        },
        {
          label: "View Our Work",
          href: "/our-work/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "trust",
      type: "trust",
      eyebrow: "Professional workmanship",
      heading: "A contractor you can count on.",
      body: "Clear estimates, dependable scheduling, careful workmanship, and communication throughout the entire project.",
    },

    {
      id: "services",
      type: "services",
      eyebrow: "What we build",
      heading: "Construction services for projects big and small.",
      body: "From repairs and renovations to larger installations and improvement projects, we help bring your plans to life.",
      services: [
        {
          title: "Renovations & Remodeling",
          description:
            "Thoughtful upgrades and renovations designed around your property, priorities, and budget.",
          image: {
            src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
            alt: "Contractor completing renovation work",
            role: "service",
          },
          href: "/services/",
        },
        {
          title: "Construction & Installation",
          description:
            "Professional construction and installation work with careful planning and attention to detail.",
          image: {
            src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
            alt: "Construction professional working on an installation",
            role: "service",
          },
          href: "/services/",
        },
        {
          title: "Repairs & Improvements",
          description:
            "Reliable repairs, maintenance, and property improvements that protect and enhance your investment.",
          image: {
            src: "/studio/assets/stock/images/tim-mossholder-LpUJB0mEd-o-unsplash.jpg",
            alt: "Contractor performing property maintenance",
            role: "service",
          },
          href: "/services/",
        },
      ],
    },

    {
      id: "process",
      type: "process",
      eyebrow: "Our process",
      heading: "A clear path from estimate to completion.",
      body: "You should always know what happens next. Our process keeps expectations, timing, and communication clear.",
      steps: [
        {
          title: "Tell us about the project",
          description:
            "Share your goals, property details, timeline, and the work you are considering.",
        },
        {
          title: "Review the plan",
          description:
            "We evaluate the project, discuss options, and provide clear recommendations and next steps.",
        },
        {
          title: "Build with confidence",
          description:
            "We complete the work with dependable communication and attention to the details that matter.",
        },
      ],
    },

    {
      id: "projects",
      type: "projects",
      eyebrow: "Featured work",
      heading: "See what we've been building.",
      body: "Showcase completed renovations, installations, repairs, and construction projects.",
      projects: [
        {
          title: "Residential Renovation",
          description:
            "A residential improvement project completed with careful planning and professional execution.",
          image: {
            src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
            alt: "Residential construction project",
            role: "project",
          },
          href: "/our-work/",
        },
        {
          title: "Installation Project",
          description:
            "A detailed installation project designed for durability, function, and a clean finished result.",
          image: {
            src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
            alt: "Contractor installation project",
            role: "project",
          },
          href: "/our-work/",
        },
        {
          title: "Property Improvement",
          description:
            "A practical property improvement completed with dependable workmanship and attention to detail.",
          image: {
            src: "/studio/assets/stock/images/tim-mossholder-qvWnGmoTbik-unsplash.jpg",
            alt: "Completed property improvement project",
            role: "project",
          },
          href: "/our-work/",
        },
      ],
    },

    {
      id: "testimonials",
      type: "testimonials",
      eyebrow: "What clients say",
      heading: "Work people feel good recommending.",
      body: "Use this section to feature customer reviews and project feedback.",
      testimonials: [
        {
          quote:
            "The entire process was organized, professional, and easy to understand.",
          name: "Homeowner",
          detail: "Residential renovation",
        },
        {
          quote:
            "Great communication from the estimate through the finished project.",
          name: "Property Owner",
          detail: "Construction project",
        },
        {
          quote:
            "They showed up when promised and paid attention to every detail.",
          name: "Local Customer",
          detail: "Property improvement",
        },
      ],
    },

    {
      id: "service-area",
      type: "service-area",
      eyebrow: "Local construction company",
      heading: "Proudly building throughout our service area.",
      body: "We work with homeowners, property owners, and businesses throughout the surrounding communities. Contact us to confirm availability for your project.",
      buttons: [
        {
          label: "Check Project Availability",
          href: "/contact/",
          variant: "secondary",
        },
      ],
    },

    {
      id: "cta",
      type: "cta",
      eyebrow: "Planning a project?",
      heading: "Let's talk about what you want to build.",
      body: "Request an estimate and tell us about your property, project goals, and timeline.",
      buttons: [
        {
          label: "Request an Estimate",
          href: "/contact/",
          variant: "primary",
        },
      ],
    },
  ],

  pages: [
    {
      id: "about",
      name: "About",
      title: "About Us",
      slug: "/about/",
      sections: [
        {
          id: "about-hero",
          type: "hero",
          eyebrow: "About our company",
          heading: "Built on dependable work and straightforward service.",
          body: "We help property owners complete construction, renovation, and improvement projects with confidence.",
          images: [
            {
              src: "/studio/assets/stock/images/troy-allen-bLue0yFJD08-unsplash.jpg",
              alt: "Construction professional at a residential property",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request an Estimate",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "about-story",
          type: "about",
          eyebrow: "How we work",
          heading: "Good construction starts with good communication.",
          body: "We believe clients should understand the project, the plan, and what to expect along the way. That means clear communication, careful workmanship, and respect for every property.",
          images: [
            {
              src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
              alt: "Contractor working on a construction project",
              role: "about",
            },
          ],
        },

        {
          id: "about-process",
          type: "process",
          eyebrow: "What to expect",
          heading: "A professional experience from start to finish.",
          body: "We keep the process organized so you can make decisions with confidence.",
          steps: [
            {
              title: "Clear planning",
              description:
                "We take time to understand the work, priorities, and project requirements.",
            },
            {
              title: "Dependable execution",
              description:
                "The project moves forward with care, communication, and attention to detail.",
            },
            {
              title: "A finished result",
              description:
                "We complete the work with a focus on quality and a clean final walkthrough.",
            },
          ],
        },

        {
          id: "about-cta",
          type: "cta",
          eyebrow: "Ready to talk?",
          heading: "Tell us about your project.",
          body: "Reach out to discuss your ideas, timeline, and the work your property needs.",
          buttons: [
            {
              label: "Contact Us",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "services",
      name: "Services",
      title: "Our Services",
      slug: "/services/",
      sections: [
        {
          id: "services-hero",
          type: "hero",
          eyebrow: "Construction services",
          heading: "Professional work for the projects that matter.",
          body: "Renovations, installations, repairs, and improvements completed with dependable communication and workmanship.",
          images: [
            {
              src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
              alt: "Contractor completing construction work",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request an Estimate",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "services-list",
          type: "services",
          eyebrow: "What we do",
          heading: "Solutions for your property and project.",
          body: "Customize these service categories in Studio to match your company's specialties.",
          services: [
            {
              title: "Renovations & Remodeling",
              description:
                "Interior and exterior improvement projects designed around your goals and property.",
              image: {
                src: "/studio/assets/stock/images/josh-olalde-X1P1_EDNnok-unsplash.jpg",
                alt: "Renovation construction work",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Construction & Installation",
              description:
                "Professional construction and installation services with careful project planning.",
              image: {
                src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
                alt: "Construction installation work",
                role: "service",
              },
              href: "/contact/",
            },
            {
              title: "Repairs & Improvements",
              description:
                "Property repairs and upgrades completed with long-term function and quality in mind.",
              image: {
                src: "/studio/assets/stock/images/tim-mossholder-LpUJB0mEd-o-unsplash.jpg",
                alt: "Property repair work",
                role: "service",
              },
              href: "/contact/",
            },
          ],
        },

        {
          id: "services-process",
          type: "process",
          eyebrow: "How projects begin",
          heading: "Start with a clear plan.",
          body: "We make it easy to understand the next steps before work begins.",
          steps: [
            {
              title: "Project conversation",
              description:
                "Tell us what you want to accomplish and what the property needs.",
            },
            {
              title: "Scope and estimate",
              description:
                "Review the recommended approach, scope, timing, and estimate.",
            },
            {
              title: "Schedule the work",
              description:
                "Choose the right timing and move forward with clear expectations.",
            },
          ],
        },

        {
          id: "services-faq",
          type: "faq",
          eyebrow: "Common questions",
          heading: "Before your project begins.",
          body: "Use these FAQs to answer the questions customers ask most often.",
          faqs: [
            {
              question: "How do I request an estimate?",
              answer:
                "Contact us with a few details about your project and property so we can discuss the appropriate next step.",
            },
            {
              question: "What types of projects do you take on?",
              answer:
                "Customize this answer with the project types, sizes, and specialties your company handles.",
            },
            {
              question: "What areas do you serve?",
              answer:
                "Add the cities, counties, neighborhoods, or regions your company serves.",
            },
          ],
        },

        {
          id: "services-cta",
          type: "cta",
          eyebrow: "Have a project in mind?",
          heading: "Request an estimate.",
          body: "Tell us what you're planning and we'll help you determine the next step.",
          buttons: [
            {
              label: "Get Started",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "our-work",
      name: "Our Work",
      title: "Our Work",
      slug: "/our-work/",
      sections: [
        {
          id: "work-hero",
          type: "hero",
          eyebrow: "Our projects",
          heading: "Built with care. Finished with attention to detail.",
          body: "Use this page to showcase completed construction, renovations, installations, and property improvements.",
          images: [
            {
              src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
              alt: "Contractor completing a construction project",
              role: "hero",
            },
          ],
          buttons: [
            {
              label: "Request an Estimate",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },

        {
          id: "work-projects",
          type: "projects",
          eyebrow: "Featured projects",
          heading: "Work we're proud to show.",
          body: "Replace these examples with real projects, before-and-after work, and completed jobs.",
          projects: [
            {
              title: "Renovation",
              description:
                "A property renovation completed with careful planning and professional workmanship.",
              image: {
                src: "/studio/assets/stock/images/scott-blake-x-ghf9LjrVg-unsplash.jpg",
                alt: "Residential renovation project",
                role: "project",
              },
            },
            {
              title: "Installation",
              description:
                "A construction installation completed with attention to detail and durability.",
              image: {
                src: "/studio/assets/stock/images/james-sullivan-ESZRBtkQ_f8-unsplash.jpg",
                alt: "Construction installation project",
                role: "project",
              },
            },
            {
              title: "Property Improvement",
              description:
                "A finished property improvement designed to improve function and long-term value.",
              image: {
                src: "/studio/assets/stock/images/tim-mossholder-qvWnGmoTbik-unsplash.jpg",
                alt: "Completed property improvement",
                role: "project",
              },
            },
          ],
        },

        {
          id: "work-testimonials",
          type: "testimonials",
          eyebrow: "Client feedback",
          heading: "What customers say about working with us.",
          testimonials: [
            {
              quote:
                "Communication was excellent and the finished work turned out exactly as we hoped.",
              name: "Homeowner",
              detail: "Renovation project",
            },
            {
              quote:
                "Professional, dependable, and very easy to work with throughout the project.",
              name: "Property Owner",
              detail: "Construction project",
            },
            {
              quote:
                "The attention to detail made a huge difference in the final result.",
              name: "Local Customer",
              detail: "Improvement project",
            },
          ],
        },

        {
          id: "work-cta",
          type: "cta",
          eyebrow: "Your project could be next",
          heading: "Let's talk about what you're planning.",
          body: "Request an estimate and tell us about the work you have in mind.",
          buttons: [
            {
              label: "Request an Estimate",
              href: "/contact/",
              variant: "primary",
            },
          ],
        },
      ],
    },

    {
      id: "contact",
      name: "Contact",
      title: "Contact Us",
      slug: "/contact/",
      sections: [
        {
          id: "contact-hero",
          type: "hero",
          eyebrow: "Start your project",
          heading: "Tell us what you want to build.",
          body: "Share a few details about your property, project, and timeline and we'll help you determine the next step.",
          images: [
            {
              src: "/studio/assets/stock/images/troy-allen-bLue0yFJD08-unsplash.jpg",
              alt: "Contractor ready to discuss a construction project",
              role: "hero",
            },
          ],
        },

        {
          id: "contact-details",
          type: "contact",
          eyebrow: "Request an estimate",
          heading: "Let's talk about your project.",
          body: "Add your phone number, email, business hours, estimate process, and preferred contact details in Studio.",
          buttons: [
            {
              label: "Call Us",
              href: "tel:+15555555555",
              hrefField: "site.contact.phone",
              variant: "primary",
            },
            {
              label: "Email Us",
              href: "mailto:hello@example.com",
              hrefField: "site.contact.email",
              variant: "secondary",
            },
          ],
        },

        {
          id: "contact-area",
          type: "service-area",
          eyebrow: "Service area",
          heading: "Building throughout the surrounding communities.",
          body: "Add the cities, counties, neighborhoods, and regions your company serves.",
        },

        {
          id: "contact-faq",
          type: "faq",
          eyebrow: "Before you contact us",
          heading: "Helpful project information.",
          faqs: [
            {
              question: "What should I include with my estimate request?",
              answer:
                "A short description of the project, your location, approximate timing, and any photos or plans you already have can be helpful.",
            },
            {
              question: "Do you offer on-site consultations?",
              answer:
                "Customize this answer to explain your company's consultation and estimating process.",
            },
            {
              question: "How soon will I hear back?",
              answer:
                "Customize this answer with your typical response time and scheduling expectations.",
            },
          ],
        },
      ],
    },
  ],
};
